import React, { useState, useEffect } from 'react';
import { FaArrowRightLong } from 'react-icons/fa6';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { API_URL } from '../../services/api';

const Register = ({ onLogin }) => {
  const location = useLocation(); // For reading URL params
  const navigate = useNavigate(); // For cleaning URL params (optional)

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    otp: '', // Added OTP field
  });
  const [isOtpSent, setIsOtpSent] = useState(false); // To control UI flow
  const [isLoading, setIsLoading] = useState(false); // For disabling buttons during API calls
  const [otpTimer, setOtpTimer] = useState(0); // Timer for resend OTP option
  const [resendDisabled, setResendDisabled] = useState(false);

  // Effect to pre-fill form if redirected from login
  useEffect(() => {
    if (location.state?.email && location.state?.suggested_username) {
      setFormData((prev) => ({
        ...prev,
        email: location.state.email,
        name: location.state.suggested_username,
      }));
      toast.success('Please create your account to continue.');
      // Clean the state from location history to prevent re-filling on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // --- START: Add useEffect for URL error parameter (similar to Login.js) ---
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const errorParam = queryParams.get('error');

    if (errorParam) {
      const decodedError = decodeURIComponent(errorParam);
      toast.error(decodedError);

      // Optional: Clean up the URL
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('error');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [location, navigate]); // Re-run if location changes
  // --- END: Add useEffect for URL error parameter ---

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const startResendOtpTimer = () => {
    setResendDisabled(true);
    setOtpTimer(60); // 60 seconds timer
    const intervalId = setInterval(() => {
      setOtpTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(intervalId);
          setResendDisabled(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  const handleInitialSignUp = async (e) => {
    e.preventDefault();
    const { email, password, name } = formData;
    if (!email || !password || !name) {
      toast.error(
        'Please fill in all required fields (Name, Email, Password).',
      );
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // user_id,
          email,
          name,
          password,
        }),
        credentials: 'include', // Keep if your backend CORS setup requires it for cookies
      });

      const data = await response.json();

      if (!response.ok) {
        throw data.detail || data.message || 'Failed to send OTP.';
      }

      toast.success(data.message || 'OTP sent to your email. Please verify.');
      setIsOtpSent(true);
      startResendOtpTimer();
    } catch (err) {
      toast.error(
        typeof err === 'string'
          ? err
          : err.message || 'An error occurred. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtpAndCompleteSignUp = async (e) => {
    e.preventDefault();
    const { email, otp } = formData;
    if (!otp) {
      toast.error('Please enter the OTP.');
      return;
    }
    if (!email) {
      toast.error(
        'Email is missing. Please restart the signup process if this persists.',
      );
      // This case should ideally not happen if OTP was sent, as email would be in formData
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp,
        }),
        credentials: 'include', // Keep if your backend CORS setup requires it
      });

      const data = await response.json();

      if (!response.ok) {
        throw data.detail || data.message || 'OTP verification failed.';
      }

      const { token, user_id: returnedUserId, project_token } = data;
      toast.success(data.message || 'Signed Up Successfully!');
      onLogin(token, returnedUserId, project_token); // Pass token and user_id to parent
    } catch (err) {
      toast.error(
        typeof err === 'string'
          ? err
          : err.message || 'OTP verification failed. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const theme = 'light'; // future → from context
  const isDark = theme === 'dark';

  return (
    <div
      className={`
      relative flex min-h-screen w-full flex-col md:flex-row
      ${isDark ? 'bg-black' : 'bg-radial-gradient'}
    `}
    >
      {/* Dark Background Image */}
      {isDark && (
        <img
          src="../assets/auth-bg.svg"
          className="fixed inset-0 z-0 h-full w-full object-cover"
          alt="Background"
        />
      )}

      {/* LEFT — SIGNUP FORM (UNCHANGED STRUCTURE) */}
      <div className="relative z-10 flex w-full flex-col items-center p-5 md:w-1/2 md:justify-center">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-5 flex items-center">
            <img
              alt="logo"
              src="../assets/super_agi_logo.png"
              className="h-16 w-16"
            />
            <h1
              className={`${isDark ? 'text-white' : 'text-black'} font-poppins ml-2 text-lg font-bold`}
            >
              SuperManager AGI
            </h1>
          </div>

          <div className="w-full">
            <h2
              className={`${isDark ? 'text-white' : 'text-black'} mb-6 text-2xl font-bold`}
            >
              Setup your account
            </h2>

            {/* FORM */}
            {!isOtpSent ? (
              <>
                {/* NAME */}
                <div className="group mb-4">
                  <label
                    htmlFor="full-name"
                    className={`mb-1 block text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                  >
                    Full Name
                  </label>
                  <input
                    htmlFor="full-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isLoading}
                    placeholder="Enter your full name"
                    className={`
                    w-full rounded-lg border-l-2 px-2 py-3 text-sm focus:outline-none
                    ${
                      isDark
                        ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#3B47B0]'
                        : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                    }
                  `}
                  />
                </div>

                {/* EMAIL */}
                <div className="group mb-4">
                  <label
                    htmlFor="email"
                    className={`mb-1 block text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                  >
                    Email
                  </label>
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading}
                    placeholder="Enter your email"
                    className={`
                    w-full rounded-lg border-l-2 px-2 py-3 text-sm focus:outline-none
                    ${
                      isDark
                        ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#3B47B0]'
                        : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                    }
                  `}
                  />
                </div>

                {/* PASSWORD */}
                <div className="group mb-4">
                  <label
                    htmlFor="password"
                    className={`mb-1 block text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                  >
                    Password
                  </label>
                  <input
                    name="password"
                    type="password"
                    placeholder="Min. 8 chars, 1 number"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLoading}
                    className={`
                    w-full rounded-lg border-l-2 px-2 py-3 text-sm focus:outline-none
                    ${
                      isDark
                        ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                        : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                    }
                  `}
                  />
                </div>
              </>
            ) : (
              /* OTP */
              <div className="group mb-4">
                <label
                  htmlFor="OTP"
                  className={`mb-1 block text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                >
                  Enter OTP sent to {formData.email}
                </label>

                <input
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Enter OTP"
                  className={`
                  w-full rounded-lg border-l-2 px-2 py-3 text-sm focus:outline-none
                  ${
                    isDark
                      ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                      : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                  }
                `}
                />

                <button
                  onClick={handleInitialSignUp}
                  disabled={isLoading || resendDisabled}
                  className={`mt-2 text-xs ${
                    resendDisabled
                      ? 'text-zinc-500'
                      : isDark
                        ? 'text-white underline'
                        : 'text-primary-gradient underline'
                  }`}
                >
                  {resendDisabled ? `Resend OTP in ${otpTimer}s` : 'Resend OTP'}
                </button>
              </div>
            )}

            {/* BUTTON — UNCHANGED */}
            <div className="flex flex-col items-center gap-6 md:flex-row md:gap-4">
              {!isOtpSent ? (
                <button
                  onClick={handleInitialSignUp}
                  disabled={isLoading}
                  className="flex w-full items-center justify-center gap-4 rounded-3xl border bg-white p-[1px] disabled:opacity-50 md:w-auto"
                >
                  <span className="font-poppins px-6 py-2 font-medium text-black">
                    {isLoading ? 'Sending OTP...' : 'Send OTP'}
                  </span>
                  {!isLoading && (
                    <span className="mr-[1px] flex h-9 w-9 items-center justify-center rounded-full bg-primary-gradient text-lg text-white">
                      <FaArrowRightLong />
                    </span>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleVerifyOtpAndCompleteSignUp}
                  disabled={isLoading}
                  className="flex w-full items-center justify-center gap-4 rounded-3xl border bg-white p-[1px] disabled:opacity-50 md:w-auto"
                >
                  <span className="font-poppins px-6 py-2 font-medium text-black">
                    {isLoading ? 'Verifying...' : 'Verify OTP & Sign Up'}
                  </span>
                  {!isLoading && (
                    <span className="mr-[1px] flex h-9 w-9 items-center justify-center rounded-full bg-[#6a65ef] text-lg text-white">
                      <FaArrowRightLong />
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* TERMS */}
            <p className="font-poppins mt-8 text-center text-sm text-gray-400">
              By Signing up, you agree to SuperManager AGI's{' '}
              <a
                href="https://www.oqlous.com/terms-of-service"
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                terms of service
              </a>{' '}
              and{' '}
              <a
                href="https://www.oqlous.com/privacy-policy"
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              .
            </p>

            {/* LOGIN LINK */}
            <p className="font-poppins mt-10 text-center text-sm text-gray-400">
              Already a member?{' '}
              <Link
                to="/login"
                className={`${isDark ? 'text-white' : 'text-black'} underline`}
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL — LIGHT ONLY */}
      {!isDark && (
        <div className="hidden w-1/2 items-center justify-center p-16 md:flex">
          <div className="max-w-xl">
            <h1 className="mb-6 text-5xl font-semibold leading-tight text-zinc-900">
              See autonomous project management running on your real projects.
            </h1>

            <p className="mb-8 text-lg leading-relaxed text-zinc-600">
              Experience autonomous project management in action. Use the form
              to request a demo from our team and discover how our platform
              removes manual PM work, safeguards your teams, and speeds up
              delivery.
            </p>

            <p className="mb-8 text-sm text-zinc-500">
              We may request additional information to verify your identity and
              understand your organization’s needs.
            </p>

            <div className="flex gap-6 text-sm text-zinc-700">
              <span className="cursor-pointer underline">Documentation</span>
              <span className="cursor-pointer underline">FAQ</span>
              <span className="cursor-pointer underline">Resource Hub</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
